<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import SummaryMetricCards from './SummaryMetricCards.vue';
import SummaryAgentDetailTables from './SummaryAgentDetailTables.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  entityId: {
    type: [String, Number],
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const loading = ref(false);

const actionMap = {
  agent: 'summaryReports/fetchAgentSummaryReports',
  team: 'summaryReports/fetchTeamSummaryReports',
  inbox: 'summaryReports/fetchInboxSummaryReports',
  label: 'summaryReports/fetchLabelSummaryReports',
};

const getterMap = {
  agent: 'summaryReports/getAgentSummaryReports',
  team: 'summaryReports/getTeamSummaryReports',
  inbox: 'summaryReports/getInboxSummaryReports',
  label: 'summaryReports/getLabelSummaryReports',
};

const buildParams = () => {
  const params = {
    since: props.filters.from,
    until: props.filters.to,
  };
  if (props.filters.labelIds?.length) params.labelIds = props.filters.labelIds;
  if (props.filters.excludeLabelIds?.length) {
    params.excludeLabelIds = props.filters.excludeLabelIds;
  }
  if (props.filters.inboxIds?.length) params.inboxIds = props.filters.inboxIds;
  if (props.filters.teamIds?.length) params.teamIds = props.filters.teamIds;
  if (props.filters.agentIds?.length) params.agentIds = props.filters.agentIds;
  if (props.filters.channelTypes?.length) {
    params.channelTypes = props.filters.channelTypes;
  }
  if (props.filters.episodeResult && props.filters.episodeResult !== 'all') {
    params.episodeResult = props.filters.episodeResult;
  }
  if (props.filters.receiptType && props.filters.receiptType !== 'all') {
    params.receiptType = props.filters.receiptType;
  }
  if (props.filters.tagPresence && props.filters.tagPresence !== 'all') {
    params.tagPresence = props.filters.tagPresence;
  }
  if (
    props.filters.resolutionMethod &&
    props.filters.resolutionMethod !== 'all'
  ) {
    params.resolutionMethod = props.filters.resolutionMethod;
  }
  return params;
};

const fetchSummary = async () => {
  if (!props.filters.from || !actionMap[props.type]) return;

  loading.value = true;
  try {
    await store.dispatch(actionMap[props.type], buildParams());
  } finally {
    loading.value = false;
  }
};

onMounted(fetchSummary);
watch(() => props.filters, fetchSummary, { deep: true });

const entityMetrics = computed(() => {
  const rows = store.getters[getterMap[props.type]] || [];
  const row = rows.find(item => Number(item.id) === Number(props.entityId));
  return row ? [{ ...row, name: row.name }] : [];
});

const allLabels = useMapGetter('labels/getLabels');

const archiveContext = computed(() => {
  const labelTitles = (props.filters.labelIds || [])
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);
  const excludeLabelTitles = (props.filters.excludeLabelIds || [])
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);

  return {
    reportType: props.type,
    from: props.filters.from,
    to: props.filters.to,
    entityFilters: {
      agentIds: props.filters.agentIds || [],
      inboxIds: props.filters.inboxIds || [],
      teamIds: props.filters.teamIds || [],
      labelIds: props.filters.labelIds || [],
      labelTitles,
      excludeLabelIds: props.filters.excludeLabelIds || [],
      excludeLabelTitles,
      channelTypes: props.filters.channelTypes || [],
      episodeResult: props.filters.episodeResult || 'all',
      receiptType: props.filters.receiptType || 'all',
      tagPresence: props.filters.tagPresence || 'all',
      resolutionMethod: props.filters.resolutionMethod || 'all',
    },
  };
});
</script>

<template>
  <div v-if="loading" class="py-4 text-sm text-n-slate-11">
    {{ $t('SUMMARY_REPORTS.CHARTS.LOADING') }}
  </div>
  <SummaryMetricCards
    v-else-if="entityMetrics.length"
    :type="type"
    variant="detail"
    :metrics="entityMetrics"
    :archive-context="archiveContext"
  />
  <SummaryAgentDetailTables
    v-if="type === 'agent' && entityMetrics.length && !loading"
    :metrics="entityMetrics[0]"
    :archive-context="archiveContext"
  />
</template>
